
import { BaseComponent } from '../../BaseComponent';
import { FloorPlanWizardStore } from './FloorPlanInternalStore';
import { LayoutType, Pica } from '../../../constants/LayoutConstants';
import { FloorPlanStore } from '../../../stores/FloorPlanStore';
import { VisibilityStore } from '../../../stores/VisibilityStore';
import { Overlay } from '../../../constants/Enums';
import { UIHelper } from '../../../utils/UIHelper';

export interface FloorPlanSubPageProps {
    store: FloorPlanWizardStore;
    pageIndex: number;
    layoutType: LayoutType;
    submitCallback?: () => void;
}

export interface FloorPlanSubPageState {
    image_id: string;
    angle: number;
    unitString: string;
}

export interface MouseCoords {
    x: number;
    y: number;
}

interface DraggingInfo {
    index: number | null;
    x: number;
    y: number;
    pointerId: number | null;
    moved?: boolean;
}

interface ZoomInfo {
    distance: number;
    x: number;
    y: number;
    pointerId: number;
}

export interface CropPosition {
    x1: number;
    y1: number;
    x2: number;
    y2: number;
}

export enum DragType {
    BOTHARROWS = 1,
    BOTTOM_LEFT = 2,
    BOTTOM_RIGHT = 3,
    BOTTOM = 4,
    IMAGE = 5,
    LEFT = 6,
    RIGHT = 7,
    TOP = 8,
    TOP_LEFT = 9,
    TOP_RIGHT = 10,
    ZOOM = 11
}

export class FloorPlanSubPage extends BaseComponent<FloorPlanSubPageProps, FloorPlanSubPageState> {
    protected divElement: HTMLDivElement;
    protected imageCanvas: HTMLCanvasElement;
    protected overlayCanvas: HTMLCanvasElement;
    protected draggingInfo: DraggingInfo = {index: null, x: 0, y: 0, pointerId: null};
    protected currentImage: string = '';
    protected zoomInfo: ZoomInfo = {pointerId: 0, distance: 1, x: 0, y: 0};
    protected resizeTrigger: number | null = null;
    protected beforeZoomConfig: {old: number, new: number} = {old: 0, new: 0};
    protected picaToken: number | null = null;
    protected picaCanvas: HTMLCanvasElement;

    get zoom(): number {return 1; }
    set zoom(value: number) { /* set store-value */ }
    get imagePosition(): MouseCoords {return {x: 0, y: 0}; }
    set imagePosition(value: MouseCoords) { /* set store-value */ }

    public render(): JSX.Element | null {
        return null;
    }

    protected async resizeImage(token: number | null): Promise<HTMLCanvasElement | null> {
        const needPica: boolean = (
            this.picaCanvas == null ||
            Math.abs(this.picaCanvas.width - FloorPlanStore.image.width * this.zoom) >= 3 ||
            Math.abs(this.picaCanvas.height - FloorPlanStore.image.height * this.zoom) >= 3
        );

        if (needPica && this.picaToken === token) {
            const tmpCanvas: HTMLCanvasElement = document.createElement('canvas');
            tmpCanvas.width = FloorPlanStore.image.width * this.zoom;
            tmpCanvas.height = FloorPlanStore.image.height * this.zoom;
            // tmpCanvas.getContext("2d").clearRect(0, 0, this.picaCanvas.width, this.picaCanvas.height);
            return Pica.resize(
                FloorPlanStore.image,
                tmpCanvas,
                {quality: 1, cancelToken: new Promise<void>((resolve, reject) => {
                    const internalInterval: number = window.setInterval(
                        () => {
                            if (this.picaToken !== token) {
                                clearInterval(internalInterval);
                                reject();
                            }
                        },
                        50
                    );
                })}
            );
        }
        return Promise.reject(null);
    }

    protected picaDraw(
        ctx: CanvasRenderingContext2D,
        x: number, y: number,
        angle: number,
    ): void {
        UIHelper.ignoreReject(this.resizeImage(this.picaToken).then(
            (result: HTMLCanvasElement | null) => {
                if (result == null) {
                    return;
                }
                ctx.save();
                if (angle !== 0) {
                    const rotationRads: number = angle / (180 / Math.PI);
                    const angleSine: number = Math.sin(rotationRads);
                    const angleCosine: number = Math.cos(rotationRads);
                    ctx.setTransform(angleCosine, angleSine, -angleSine, angleCosine, x, y);
                    ctx.drawImage(result, 0, 0, result.width, result.height);
                } else {
                    ctx.drawImage(result, x, y, result.width, result.height);
                }
                ctx.restore();
                this.picaCanvas = result;
            }
        ));
    }

    protected drawImage(
        image: HTMLImageElement,
        x: number, y: number,
        width: number, height: number,
        angle: number,
        ctx: CanvasRenderingContext2D
    ): void {
        if (this.picaToken != null) {
            clearTimeout(this.picaToken);
            this.picaToken = null;
        }
        const usePicaCanvas: boolean = (
            this.picaCanvas != null &&
            Math.abs(this.picaCanvas.width - width) < 3 &&
            Math.abs(this.picaCanvas.height - height) < 3
        );
        if (angle !== 0) {
            const rotationRads: number = angle / (180 / Math.PI);
            const angleSine: number = Math.sin(rotationRads);
            const angleCosine: number = Math.cos(rotationRads);
            ctx.setTransform(angleCosine, angleSine, -angleSine, angleCosine, x, y);
            ctx.drawImage(
                (usePicaCanvas ? this.picaCanvas : image),
                0, 0,
                width,
                height,
            );
        } else {
            ctx.drawImage(
                (usePicaCanvas ? this.picaCanvas : image),
                x, y,
                width,
                height
            );
        }
        if (!usePicaCanvas) {
            this.picaToken = window.setTimeout(() => { this.picaDraw(ctx, x, y, angle); }, 100);
        }
    }

    protected redrawImage(rotateAngle: boolean): void {
        if (
            this.imageCanvas != null &&
            this.state != null &&
            VisibilityStore.getState().overlay === Overlay.FLOORPLAN
        ) {
            const ctxImage: CanvasRenderingContext2D | null = this.imageCanvas.getContext('2d');
            if (ctxImage == null) {
                return;
            }
            ctxImage.clearRect(0, 0, this.imageCanvas.width, this.imageCanvas.height);
            ctxImage.save();
            const rotationAngle: number = this.props.store.getRotation(rotateAngle);
            this.drawImage(
                FloorPlanStore.image,
                this.imagePosition.x, this.imagePosition.y,
                Math.ceil(FloorPlanStore.image.width * this.zoom),
                Math.ceil(FloorPlanStore.image.height * this.zoom),
                rotationAngle,
                ctxImage
            );
            ctxImage.restore();
        }
    }

    protected clearResizeInterval = (): void => {
        if (this.resizeTrigger != null) {
            clearInterval(this.resizeTrigger);
            this.resizeTrigger = null;
        }
    };

    protected onResize = (): void => {
        this.clearResizeInterval();

        this.redraw();

        let counter: number = 0;
        this.resizeTrigger = window.setInterval(
            () => {
                counter++;
                if (
                    this.divElement.clientWidth !== this.imageCanvas.width ||
                    this.divElement.clientHeight !== this.imageCanvas.height
                ) {
                    this.clearResizeInterval();
                    this.redraw();
                } else if (counter > 50) { // 50 * 100 ms -> 5s
                    this.clearResizeInterval();
                }
            },
            100
        );
    };

    protected centerImage(canvas: HTMLCanvasElement, imageWidth: number, imageHeight: number, zoom: number): MouseCoords {
        // Upper left corner of image
        const x1: number = .5 * canvas.width - imageWidth * .5 * zoom;
        const y1: number = .5 * canvas.height - imageHeight * .5 * zoom;
        // Center of image
        const x2: number = .5 * canvas.width;
        const y2: number = .5 * canvas.height;
        // Delta of center to upper-left
        const dx2: number = x2 - x1;
        const dy2: number = y2 - y1;
        // Delta of center to upper-left after rotation
        const dx2a: number =
            dx2 * Math.cos(-this.props.store.getRotation(true) * (Math.PI / 180)) +
            dy2 * Math.sin(-this.props.store.getRotation(true) * (Math.PI / 180));
        const dy2a: number =
            -(dx2 * Math.sin(-this.props.store.getRotation(true) * (Math.PI / 180)) -
            dy2 * Math.cos(-this.props.store.getRotation(true) * (Math.PI / 180)));
        return ({
            x: x1 + (dx2 - dx2a),
            y: y1 + (dy2 - dy2a)
        });
    }

    protected scaleImageData(ctx: CanvasRenderingContext2D, imageData: ImageData, scale: number): ImageData {
        const scaled: ImageData = ctx.createImageData(imageData.width * scale, imageData.height * scale);
        for (let row: number = 0; row < imageData.height; row++) {
            for (let col: number = 0; col < imageData.width; col++) {
                const sourcePixel: Array<number> = [
                    imageData.data[(row * imageData.width + col) * 4 + 0],
                    imageData.data[(row * imageData.width + col) * 4 + 1],
                    imageData.data[(row * imageData.width + col) * 4 + 2],
                    imageData.data[(row * imageData.width + col) * 4 + 3]
                ];
                for (let y: number = 0; y < scale; y++) {
                    const destRow: number = row * scale + y;
                    for (let x: number = 0; x < scale; x++) {
                        const destCol: number = col * scale + x;
                        for (let i: number = 0; i < 4; i++) {
                            scaled.data[(destRow * scaled.width + destCol) * 4 + i] = sourcePixel[i];
                        }
                    }
                }
            }
        }
        return scaled;
    }

    protected getPointerCoords(event: PointerEvent | MouseWheelEvent): {x: number, y: number} {
        let element: HTMLCanvasElement = this.overlayCanvas;
        let offsetX: number = 0;
        let offsetY: number = 0;
        let mx: number;
        let my: number;

        if (element != null && element.offsetParent != null) {
            do {
                offsetX += element.offsetLeft;
                offsetY += element.offsetTop;
            } while ((element = element.offsetParent as HTMLCanvasElement));
        }
        mx = event.pageX - offsetX;
        my = event.pageY - offsetY;
        return {x: mx, y: my};
    }

    protected getDistance(coord1: MouseCoords, coord2: MouseCoords): number {
        const a: number = Math.abs(coord1.x - coord2.x);
        const b: number = Math.abs(coord1.y - coord2.y);
        return Math.sqrt( a * a + b * b );
    }

    protected setZoom(zoom: number, zoomPoint: MouseCoords | null, save?: boolean): void {
        if (
            FloorPlanStore.image.width * zoom > 50 &&
            FloorPlanStore.image.height * zoom > 50
        ) {
            if (zoomPoint != null) {
                const multiplicatorX: number = (zoomPoint.x - this.imagePosition.x) / this.zoom;
                this.imagePosition.x = zoomPoint.x - multiplicatorX * zoom;
                const multiplicatorY: number = (zoomPoint.y - this.imagePosition.y) / this.zoom;
                this.imagePosition.y = zoomPoint.y - multiplicatorY * zoom;
            }
            if (save) {
                this.beforeZoomConfig = {old: this.zoom, new: zoom};
            }
            this.zoom = zoom;
        }
    }

    protected mouseWheel = (event: MouseWheelEvent): void => {
        const zoomPoint: MouseCoords = this.getPointerCoords(event);
        if (event.deltaY > 0) {
            if (this.zoom > .1) { this.setZoom(this.zoom - .1, zoomPoint); }
        } else {
            this.setZoom(this.zoom + .1, zoomPoint);
        }
        this.redraw();
    };

    protected basePointerMove(event: PointerEvent): void {
        const coords: {x: number, y: number} = this.getPointerCoords(event);
        if (this.draggingInfo.index === DragType.IMAGE) {
            if (event.pointerId === this.draggingInfo.pointerId) { // change imagePosition | draggingInfo moved
                this.imagePosition = {
                    x: coords.x - this.draggingInfo.x + this.imagePosition.x,
                    y: coords.y - this.draggingInfo.y + this.imagePosition.y
                };
                this.draggingInfo = {
                    index: DragType.IMAGE,
                    x: coords.x,
                    y: coords.y,
                    pointerId: event.pointerId
                };
            }
        } else if (this.draggingInfo.index === DragType.ZOOM) {
            const oldMiddle: MouseCoords = this.getMiddle(
                {x: this.zoomInfo.x, y: this.zoomInfo.y},
                {x: this.draggingInfo.x, y: this.draggingInfo.y}
            );
            let newMiddle: MouseCoords | null = null;
            if (event.pointerId === this.draggingInfo.pointerId) { // change imagePosition | draggingInfo moved
                newMiddle = this.getMiddle(
                    {x: this.zoomInfo.x, y: this.zoomInfo.y},
                    {x: coords.x, y: coords.y}
                );
                this.draggingInfo.x = coords.x;
                this.draggingInfo.y = coords.y;
            } else if (event.pointerId === this.zoomInfo.pointerId) { // change zoom | ZoomInfo moved
                newMiddle = this.getMiddle(
                    {x: coords.x, y: coords.y},
                    {x: this.draggingInfo.x, y: this.draggingInfo.y}
                );
                this.zoomInfo.x = coords.x;
                this.zoomInfo.y = coords.y;
            }
            if (newMiddle != null) {
                const diffX: number = oldMiddle.x - newMiddle.x;
                const diffY: number = oldMiddle.y - newMiddle.y;
                this.imagePosition = {
                    x: this.imagePosition.x - diffX,
                    y: this.imagePosition.y - diffY
                };
                this.setZoom(
                    this.zoom * this.getDistance(
                        {x: this.zoomInfo.x, y: this.zoomInfo.y},
                        {x: this.draggingInfo.x, y: this.draggingInfo.y}
                    ) / this.zoomInfo.distance,
                    newMiddle
                );
                this.zoomInfo.distance = this.getDistance(
                    {x: this.zoomInfo.x, y: this.zoomInfo.y},
                    {x: this.draggingInfo.x, y: this.draggingInfo.y}
                );
            }
        }
    }

    protected contextMenu(event: MouseEvent): void {
        event.preventDefault();
    }

    protected pointerLeave = (event: PointerEvent): void => {
        if (event.target && (event.target as HTMLDivElement).className === 'fp-canvas') {
            if (this.zoomInfo.pointerId === event.pointerId && this.draggingInfo.index === DragType.ZOOM) {
                this.draggingInfo.index = DragType.IMAGE;
                this.redraw();
            } else {
                this.draggingInfo.index = null;
                this.redraw();
            }
        }
    };

    protected getMiddle(coordA: MouseCoords, coordB: MouseCoords): MouseCoords {
        return ({
            x: (coordA.x + (coordB.x - coordA.x) * .5),
            y: (coordA.y + (coordB.y - coordA.y) * .5)
        });
    }

    protected getRelativePosition(stdCoords: MouseCoords): MouseCoords {
        return {
            x: (stdCoords.x - this.imagePosition.x) / this.zoom,
            y: (stdCoords.y - this.imagePosition.y) / this.zoom,
        };
    }

    protected redraw = (noImage?: boolean): void => { /* implement me */ };

    protected pointerDown = (event: PointerEvent): void => {
        const originalElement: HTMLDivElement = (event.target as HTMLDivElement);
        const coords: MouseCoords = this.getPointerCoords(event);
        if (this.draggingInfo.index == null) {
            switch (originalElement.id) {
                case 'left-arrow':
                    const leftPos: MouseCoords = this.getRelativePosition(coords);
                    this.draggingInfo = {
                        index: DragType.LEFT,
                        x: leftPos.x - this.props.store.arrowPositions[0].x,
                        y: leftPos.y - this.props.store.arrowPositions[0].y,
                        pointerId: event.pointerId
                    };
                    break;
                case 'right-arrow':
                    const rightPos: MouseCoords = this.getRelativePosition(coords);
                    this.draggingInfo = {
                        index: DragType.RIGHT,
                        x: rightPos.x - this.props.store.arrowPositions[1].x,
                        y: rightPos.y - this.props.store.arrowPositions[1].y,
                        pointerId: event.pointerId
                    };
                    break;
                case 'top-left-interactor':
                    this.draggingInfo = {
                        index: DragType.TOP_LEFT,
                        x: coords.x - this.props.store.cropPosition.x1,
                        y: coords.y - this.props.store.cropPosition.y1,
                        pointerId: event.pointerId
                    };
                    break;
                case 'top-right-interactor':
                    this.draggingInfo = {
                        index: DragType.TOP_RIGHT,
                        x: coords.x - this.props.store.cropPosition.x2,
                        y: coords.y - this.props.store.cropPosition.y1,
                        pointerId: event.pointerId
                    };
                    break;
                case 'bottom-left-interactor':
                    this.draggingInfo = {
                        index: DragType.BOTTOM_LEFT,
                        x: coords.x - this.props.store.cropPosition.x1,
                        y: coords.y - this.props.store.cropPosition.y2,
                        pointerId: event.pointerId
                    };
                    break;
                case 'bottom-right-interactor':
                    this.draggingInfo = {
                        index: DragType.BOTTOM_RIGHT,
                        x: coords.x - this.props.store.cropPosition.x2,
                        y: coords.y - this.props.store.cropPosition.y2,
                        pointerId: event.pointerId
                    };
                    break;
                case 'right-interactor':
                    this.draggingInfo = {
                        index: DragType.RIGHT,
                        x: coords.x - this.props.store.cropPosition.x2,
                        y: coords.y - (this.props.store.cropPosition.y1 + .5 * (this.props.store.cropPosition.y2 - this.props.store.cropPosition.y1)),
                        pointerId: event.pointerId
                    };
                    break;
                case 'left-interactor':
                    this.draggingInfo = {
                        index: DragType.LEFT,
                        x: coords.x - this.props.store.cropPosition.x1,
                        y: coords.y - (this.props.store.cropPosition.y1 + .5 * (this.props.store.cropPosition.y2 - this.props.store.cropPosition.y1)),
                        pointerId: event.pointerId
                    };
                    break;
                case 'top-interactor':
                    this.draggingInfo = {
                        index: DragType.TOP,
                        x: coords.x - (this.props.store.cropPosition.x1 + .5 * (this.props.store.cropPosition.x2 - this.props.store.cropPosition.x1)),
                        y: coords.y - this.props.store.cropPosition.y1,
                        pointerId: event.pointerId
                    };
                    break;
                case 'bottom-interactor':
                    this.draggingInfo = {
                        index: DragType.BOTTOM,
                        x: coords.x - (this.props.store.cropPosition.x1 + .5 * (this.props.store.cropPosition.x2 - this.props.store.cropPosition.x1)),
                        y: coords.y - this.props.store.cropPosition.y2,
                        pointerId: event.pointerId
                    };
                    break;
                default:
                    this.draggingInfo = {
                        index: DragType.IMAGE,
                        x: coords.x,
                        y: coords.y,
                        pointerId: event.pointerId
                    };
                    break;
            }
        } else if (
            (this.draggingInfo.index === DragType.ZOOM || this.draggingInfo.index === DragType.IMAGE) &&
            this.draggingInfo.pointerId !== event.pointerId
        ) {
            this.zoomInfo = {
                pointerId: event.pointerId,
                distance: this.getDistance(
                    {x: coords.x, y: coords.y},
                    {x: this.draggingInfo.x, y: this.draggingInfo.y}
                ),
                x: coords.x,
                y: coords.y
            };
            this.draggingInfo.index = DragType.ZOOM;
        }
    };

    protected setDivElement = (node: HTMLDivElement): void => {
        this.divElement = node;
    };
}


// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/components/overlay/floorplan/FloorPlanSubPage.tsx